import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconBuildingWarehouse, IconPlus, IconEdit, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const emptyForm = { name: '', code: '', branch: '', address: '', is_default: false };

const Warehouses = () => {
    const [appId, setAppId] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [branches, setBranches] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingWarehouse, setEditingWarehouse] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadWarehouses();
        loadBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadWarehouses = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
            setWarehouses(res.data || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar depósitos' });
        }
    };

    const loadBranches = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/branches/`);
            setBranches(res.data.branches || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenCreate = () => {
        setEditingWarehouse(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const handleOpenEdit = (warehouse) => {
        setEditingWarehouse(warehouse);
        setForm({
            name: warehouse.name,
            code: warehouse.code || '',
            branch: warehouse.branch || '',
            address: warehouse.address || '',
            is_default: !!warehouse.is_default
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        const payload = { ...form, branch: form.branch || null };
        try {
            if (editingWarehouse) {
                await api.patch(`v1/apps/${appId}/inventory/warehouses/${editingWarehouse.id}/`, payload);
            } else {
                await api.post(`v1/apps/${appId}/inventory/warehouses/`, payload);
            }
            setDialogOpen(false);
            loadWarehouses();
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || Object.values(error.response?.data || {})[0] || 'Erro ao salvar depósito'
            });
        }
    };

    const handleDelete = async (warehouse) => {
        if (!window.confirm(`Excluir o depósito "${warehouse.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/inventory/warehouses/${warehouse.id}/`);
            setWarehouses((prev) => prev.filter((w) => w.id !== warehouse.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.detail || 'Erro ao excluir depósito' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuildingWarehouse}
                gradient="amber"
                title="Depósitos"
                subtitle="Locais físicos onde o estoque é mantido"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Novo depósito
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Código</TableCell>
                                <TableCell>Filial</TableCell>
                                <TableCell>Padrão</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {warehouses.map((warehouse) => (
                                <TableRow key={warehouse.id} hover>
                                    <TableCell>{warehouse.name}</TableCell>
                                    <TableCell>{warehouse.code || '—'}</TableCell>
                                    <TableCell>{warehouse.branch_name || '—'}</TableCell>
                                    <TableCell>
                                        {warehouse.is_default ? <Chip label="Padrão" color="primary" size="small" /> : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(warehouse)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(warehouse)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {warehouses.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhum depósito cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingWarehouse ? 'Editar depósito' : 'Novo depósito'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Nome"
                            fullWidth
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <TextField
                            label="Código (opcional)"
                            fullWidth
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value })}
                        />
                        <TextField
                            select
                            fullWidth
                            label="Filial (opcional)"
                            value={form.branch}
                            onChange={(e) => setForm({ ...form, branch: e.target.value })}
                        >
                            <MenuItem value="">Nenhuma</MenuItem>
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Endereço (opcional)"
                            fullWidth
                            value={form.address}
                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!form.is_default}
                                    onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                                />
                            }
                            label="Depósito padrão da conta"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Warehouses;
